import { useEffect, useState } from 'react';
import i18n from '../i18n';

/**
 * Helper function to get cookie value
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

/**
 * Persist user data fields into localStorage
 */
const storeUserData = (userData) => {
  localStorage.setItem('user', JSON.stringify(userData));
  if (userData.companyId) {
    localStorage.setItem('companyId', userData.companyId.toString());
  }
  if (userData.platformRole) {
    localStorage.setItem('platformRole', userData.platformRole);
  }
  if (userData.companyRole) {
    localStorage.setItem('companyRole', userData.companyRole);
  }
  if (userData.hasCmmsSubscription !== undefined) {
    localStorage.setItem('hasCmmsSubscription', userData.hasCmmsSubscription.toString());
  }
  if (userData.hasChatbotSubscription !== undefined) {
    localStorage.setItem('hasChatbotSubscription', userData.hasChatbotSubscription.toString());
  }
};

/**
 * AuthInitializer - Loads user data from cookie, URL param, or API before app renders
 */
const AuthInitializer = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      console.log('🔍 AuthInitializer - Starting...');

      // 1. Try to load from userData cookie
      const userDataCookie = getCookie('userData');
      if (userDataCookie) {
        try {
          console.log('📥 Found userData cookie, decoding...');
          const userData = JSON.parse(atob(userDataCookie));
          console.log('✅ User data from cookie:', userData);
          storeUserData(userData);
          console.log('✅ User data stored in localStorage from cookie');
        } catch (err) {
          console.error('❌ Failed to parse userData cookie:', err);
        }
      } else {
        console.log('ℹ️ No userData cookie found');
      }

      // 2. Check for auth data in URL (fallback)
      const params = new URLSearchParams(window.location.search);
      const authData = params.get('auth');
      if (authData) {
        try {
          console.log('📥 Found auth data in URL, decoding...');
          const userData = JSON.parse(atob(authData));
          console.log('✅ User data from URL:', userData);
          storeUserData(userData);
          console.log('✅ User data stored in localStorage from URL');

          // Clean URL
          params.delete('auth');
          const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          window.history.replaceState({}, '', newUrl);
          console.log('✅ URL cleaned');
        } catch (err) {
          console.error('❌ Failed to parse auth data from URL:', err);
        }
      }

      // 3. Always re-fetch subscription flags from the backend so cancellations / new
      //    subscriptions are reflected immediately after a page reload.
      const existingUser = localStorage.getItem('user');
      const existingCompanyId = localStorage.getItem('companyId');
      if (existingUser && existingCompanyId) {
        console.log('ℹ️ User exists — refreshing subscription flags from subscription API...');
        try {
          const companyId = existingCompanyId;
          const [cmmsRes, chatbotRes] = await Promise.all([
            fetch(`/api/subscriptions/cmms/company/${companyId}`, { credentials: 'include' }).catch(() => null),
            fetch(`/api/subscriptions/chatbot/company/${companyId}`, { credentials: 'include' }).catch(() => null)
          ]);
          const hasCmms = cmmsRes?.ok === true;
          const hasChatbot = chatbotRes?.ok === true;

          localStorage.setItem('hasCmmsSubscription', String(hasCmms));
          localStorage.setItem('hasChatbotSubscription', String(hasChatbot));

          if (hasCmms && cmmsRes) {
            try {
              const cmmsData = await cmmsRes.json();
              localStorage.setItem('cmmsSubscriptionStatus', cmmsData.status ?? '');
            } catch { localStorage.removeItem('cmmsSubscriptionStatus'); }
          } else {
            localStorage.removeItem('cmmsSubscriptionStatus');
          }

          if (hasChatbot && chatbotRes) {
            try {
              const chatbotData = await chatbotRes.json();
              localStorage.setItem('chatbotIncludedAgents', String(chatbotData.includedAgents ?? 'null'));
              localStorage.setItem('chatbotSubscriptionStatus', chatbotData.status ?? '');
            } catch { localStorage.removeItem('chatbotIncludedAgents'); localStorage.removeItem('chatbotSubscriptionStatus'); }
          } else {
            localStorage.removeItem('chatbotIncludedAgents');
            localStorage.removeItem('chatbotSubscriptionStatus');
          }

          console.log('✅ Subscription flags updated:', { hasCmmsSubscription: hasCmms, hasChatbotSubscription: hasChatbot });
        } catch (err) {
          console.error('❌ Failed to fetch subscription flags:', err);
        }
      }

      // 4. If still no user data, fetch from API using the auth cookie
      if (!localStorage.getItem('user')) {
        console.log('ℹ️ No user data found — fetching from /api/users/me...');
        try {
          const response = await fetch('/api/users/me', { credentials: 'include' });
          if (response.ok) {
            const userData = await response.json();
            console.log('✅ User data from API:', userData);
            storeUserData(userData);
            console.log('✅ User data stored in localStorage from API');

            // Also fetch subscription status (not in UserResponse)
            if (userData.companyId) {
              const companyId = userData.companyId;
              const [cmmsRes, chatbotRes] = await Promise.all([
                fetch(`/api/subscriptions/cmms/company/${companyId}`, { credentials: 'include' }).catch(() => null),
                fetch(`/api/subscriptions/chatbot/company/${companyId}`, { credentials: 'include' }).catch(() => null)
              ]);
              const hasCmms = cmmsRes?.ok === true;
              const hasChatbot = chatbotRes?.ok === true;
              localStorage.setItem('hasCmmsSubscription', String(hasCmms));
              localStorage.setItem('hasChatbotSubscription', String(hasChatbot));

              if (hasCmms && cmmsRes) {
                try {
                  const cmmsData = await cmmsRes.json();
                  localStorage.setItem('cmmsSubscriptionStatus', cmmsData.status ?? '');
                } catch { localStorage.removeItem('cmmsSubscriptionStatus'); }
              } else {
                localStorage.removeItem('cmmsSubscriptionStatus');
              }

              if (hasChatbot && chatbotRes) {
                try {
                  const chatbotData = await chatbotRes.json();
                  localStorage.setItem('chatbotIncludedAgents', String(chatbotData.includedAgents ?? 'null'));
                  localStorage.setItem('chatbotSubscriptionStatus', chatbotData.status ?? '');
                } catch { localStorage.removeItem('chatbotIncludedAgents'); localStorage.removeItem('chatbotSubscriptionStatus'); }
              } else {
                localStorage.removeItem('chatbotIncludedAgents');
                localStorage.removeItem('chatbotSubscriptionStatus');
              }

              console.log('✅ Subscription flags set from API:', { hasCmmsSubscription: hasCmms, hasChatbotSubscription: hasChatbot });
            }
          } else {
            console.log('ℹ️ /api/users/me returned', response.status, '— user not authenticated');
          }
        } catch (err) {
          console.error('❌ Failed to fetch user data from API:', err);
        }
      }

      // 5. Apply preferredLanguage from backend — backend is source of truth for language
      const finalUserRaw = localStorage.getItem('user');
      if (finalUserRaw) {
        try {
          const finalUser = JSON.parse(finalUserRaw);
          if (finalUser.preferredLanguage && finalUser.preferredLanguage !== i18n.language) {
            console.log('🌐 Applying preferredLanguage from backend:', finalUser.preferredLanguage);
            i18n.changeLanguage(finalUser.preferredLanguage);
          }
        } catch {
          // ignore — language stays at browser-detected value
        }
      }

      // Check final state
      const finalUser = localStorage.getItem('user');
      console.log('📊 Final localStorage user:', finalUser ? 'Found ✅' : 'Not found ❌');

      setInitialized(true);
    };

    init();
  }, []);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Initializing...</p>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthInitializer;
